import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableItem from "../../Components/Database/SortableItem"; // Import the SortableItem component

import { toast } from "react-toastify";
import Loader from "../../Components/Services/Loader";
import { useSelector } from "react-redux";
import axios from "axios";
import { FaTrash } from "react-icons/fa";
import ReactQuill from "react-quill";

const Productitem = () => {
  const { product_id } = useParams(); // Get product_id from URL params
  const [isInsertingSymbol, setIsInsertingSymbol] = useState(false);
  const [product, setProduct] = useState(null);
  const [ingredients, setIngredients] = useState([]); // To store the list of ingredients
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [originalProduct, setOriginalProduct] = useState(null);

  // Identified Uses states
  const [identifiedUsesData, setIdentifiedUsesData] = useState([]);
  const [identifiedUses, setIdentifiedUses] = useState("");
  const [customIdentifiedUses, setCustomIdentifiedUses] = useState("");
  const [showIdentifiedInput, setShowIdentifiedInput] = useState(false);

  // Mixtures Uses states
  const [mixturesData, setMixturesData] = useState([]);
  const [mixtures, setMixtures] = useState("");
  const [customMixtures, setCustomMixtures] = useState("");
  const [showMixturesInput, setShowMixturesInput] = useState(false);

  // Appearance Data states
  const [appearanceData, setAppearanceData] = useState([]);
  const [appearance, setAppearance] = useState("");
  const [customAppearance, setCustomAppearance] = useState("");
  const [showAppearanceInput, setShowAppearanceInput] = useState(false);

  // Color states
  const [colorData, setColorData] = useState([]);
  const [color, setColor] = useState("");
  const [customColor, setCustomColor] = useState("");
  const [showColorInput, setShowColorInput] = useState(false);

  //Symbol States
  const [symbolData, setSymbolData] = useState([]);
  const [selectedSymbols, setSelectedSymbols] = useState([
    { symbol: "0", symbol_code: "0000", symbol_id: 0 },
  ]);
  const [productName, setProductName] = useState("");

  //Original order
  const [originalOrder, setOriginalOrder] = useState([]);
  const [isSaving, setIsSaving] = useState(false); // Track saving state

  const quillRefs = useRef([]);
  const { role } = useSelector((state) => state.auth);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Drag activates after moving 5px (avoids accidental taps)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor)
  );

  const measuringConfig = {
    droppable: {
      strategy: MeasuringStrategy.Always,
    },
  };

  const allInfoFetch = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URI}/elixir/all-info`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (response.status !== 200) {
        console.error("Unable to fetch data, please refresh page");
      }

      setIdentifiedUsesData(response.data.identified_uses);
      setMixturesData(response.data.mixtures);
      setAppearanceData(response.data.appearance);
      setColorData(response.data.color);
      setSymbolData(response.data.symbols);
    } catch (error) {
      throw new Error("Unable to fetch data");
    }
  };

  const handleDropDown = (
    value,
    setShowInput,
    setSelectedValue,
    setCustomInput,
    name
  ) => {
    if (value === "Other") {
      setShowInput(true);
      setSelectedValue("");
    } else {
      setShowInput(false);
      setCustomInput("");
      setSelectedValue(value);
    }
  };

  const handleInputEvent = (
    e,
    customInput,
    setSelectedValue,
    setShowInput,
    name
  ) => {
    if (e.key === "Enter" && customInput.trim()) {
      let customValue = customInput.trim();
      setSelectedValue(customValue);
      setShowInput(false);
    }
  };
  const fetchProduct = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URI}/elixir/fetch_items/${product_id}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Product not found");
      }

      const data = await response.json();

      let sortedIngredients = data.data.sort((a, b) => a.seq_no - b.seq_no); //sort the ingredients

      sortedIngredients.forEach((curr) => {
        if (curr.alpha_composition === null) {
          curr.alpha_composition = "";
        }
        delete curr.updated_at;
      });

      setProduct(data.data[0]); // Set the main product details
      setProductName(data.data[0].product_name);
      setIngredients(sortedIngredients); // Assuming the API returns all ingredients for this product
      setOriginalOrder(sortedIngredients);
      setIdentifiedUses(data.data[0].identified_uses);
      setMixtures(data.data[0].mixtures);
      setAppearance(data.data[0].appearance);
      setColor(data.data[0].color);
    } catch (error) {
      setError(error.message);
    }
  };

  useEffect(() => {
    fetchProduct();
    allInfoFetch();
  }, [product_id]);

  useEffect(() => {
    if (!symbolData || !product) return; // Ensure data is available

    const matchedSymbols = symbolData.filter(
      (item) => item.symbol_id === product.symbol_id
    );

    setSelectedSymbols(
      matchedSymbols.length > 0
        ? matchedSymbols
        : product.symbol_id === 0 && [
            { symbol: "0", symbol_code: "0000", symbol_id: 0 },
          ]
    );
  }, [symbolData, product]); // Runs when `symbolData` or `product` changes

  //Handle update product item
  const handlePatch = async () => {
    try {
      if (!ingredients || ingredients.length === 0) {
        toast.error("No ingredients to update!");
        return;
      }

      if (product.product_id.trim() === "" || product.product_id === " ") {
        toast.error("Product ID must not be empty!");
        return;
      }

      const payload = {
        // product_id,
        // identified_uses: identifiedUses,
        // mixtures: mixtures,
        // appearance: appearance,
        // color: color,
        // ingredients: ingredients,
        // created_at: product.created_at,
        // updated_at: new Date().toISOString(),

        data: {
          product_id: product.product_id.trim(),
          ingredients: ingredients,
          created_at: product.created_at,
          updated_at: new Date().toISOString(),
        },
        data1: {
          product_id: product.product_id.trim(),
          product_name: productName.trim(),
          symbol_id: selectedSymbols[0].symbol_id,
          identified_uses_of_application: identifiedUses.trim(),
          mixtures_of_application: mixtures.trim(),
          product_appearance: appearance.trim(),
          product_color: color.trim(),
          created_at: product.created_at,
          updated_at: new Date().toISOString(),
        },
      };

    

      const isValidIngredients = ingredients.every(
        (ingredient) =>
          /^[A-Z0-9\W_]+$/u.test(ingredient.ing_item_code) &&
          !isNaN(ingredient.per_composition) &&
          ingredient.per_composition >= 0 &&
          ingredient.per_composition <= 100
      );

      if (!isValidIngredients) {
        alert("Invalid ingredient details. Please check compositions.");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URI}/elixir/item/${product_id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
          },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      // const data = await response.json();

      // if(response.status === 500){
      //   toast.error("Internal Server Error, Please Check Data Before Updating")
      //   return
      // }

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData?.detail?.includes("Duplicate entry")) {
          alert(
            "Duplicate Item Code or Product ID! Please check inputs before submitting."
          );
          toast.error(
            "❌ Duplicate Item Code or Product ID! Please check inputs before submitting."
          );
          return;
        }

        toast.error(
          `Error: ${errorData.message || "Failed to update ingredient"}`
        );
        throw new Error(
          `HTTP error! Status: ${response.status}, Message: ${JSON.stringify(
            errorData
          )}`
        );
      }

      if (response.ok) {
        let responseData = null;
        try {
          responseData = await response.json(); // Parse response JSON safely

          setProduct((prev) => ({
            ...prev,
            product_name: productName,
            identified_uses: identifiedUses,
            mixtures: mixtures,
            appearance: appearance,
            color: color,
            symbol_id: selectedSymbols[0].symbol_id || 0,
            updated_at: new Date().toISOString(),
          }));
          setIngredients([...ingredients]);
          setOriginalOrder([...ingredients]);
          alert("Product updated successfully");
          toast.success("Product updated successfully!");
        } catch {
          responseData = {
            message: "Update successful, but no JSON returned.",
          };
        }
      }

      setEditMode(false);

      navigate(`/productitem/${product.product_id.trim()}`);

      // alert("Ingredient updated successfully!");
      // setEditedIngredient(null); // Reset edit mode
      // const updatedIngredients = ingredients.map((ing) =>
      //   ing.ing_item_code === ingredient.ing_item_code ? ingredient : ing
      // );
      // setIngredients(updatedIngredients);
    } catch (error) {
      console.error("Error when update", error);
      if (error.message.includes("Failed to fetch")) {
        toast.error("Network error! Please check your internet connection.");
      } else {
        toast.error(error.message || "Failed to update the ingredient.");
      }
      setError(error.message || "Failed to update the ingredient");
    } finally {
      setIsSaving(false);
    }
  };

  //Handle delete product details
  const handleDelete = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this product?"
    );
    if (confirmDelete) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URI}/elixir/product/${product_id}`,
          {
            method: "DELETE",
            headers: {
              accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete the product");
        }

        alert("Product deleted successfully");
        navigate("/database"); // Redirect to the database page after deletion
      } catch (error) {
        if (error.message.includes("Failed to fetch")) {
          toast.error("Network error! Please check your internet connection.");
        } else {
          toast.error(error.message || "Failed to delete the product.");
        }
        setError("Failed to delete the product");
      }
    }
  };

  //Handle drag and drop functionality
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    setIngredients((prevIngredients) => {
      // Find old and new index positions
      const oldIndex = prevIngredients.findIndex(
        (item) => item.seq_no === Number(active.id)
      );
      const newIndex = prevIngredients.findIndex(
        (item) => item.seq_no === Number(over.id)
      );

      // Reorder ingredients & update seq_no
      const updatedIngredients = arrayMove(
        prevIngredients,
        oldIndex,
        newIndex
      ).map((item, index) => ({
        ...item,
        seq_no: index + 1,
        created_at: item.created_at,
        updated_at: new Date().toISOString(), // Ensure `seq_no` is sequential after reordering
      }));

      return updatedIngredients;
    });
  };

  //Handle ingredient edits
  const handleEditChange = (e, ingredient) => {
    const { name, value, type, checked } = e.target;

    // ingredient.updated_at = new Date().toISOString();

    const updatedValue = type === "checkbox" ? (checked ? 1 : 0) : value;
    // if (name === "ing_item_code") {

    // }

    // const updatedIngredients = ingredients.map((ing) =>
    //   ing.seq_no === ingredient.seq_no
    //     ? {
    //       ...ing,
    //       [name]: updatedValue,
    //       alpha_composition: ing.alpha_composition || ""
    //     }
    //     : ing
    // );

    // // updatedIngredients

    // setIngredients(updatedIngredients);
    setIngredients((prev) =>
      prev.map((ing) =>
        ing.seq_no === ingredient.seq_no
          ? {
              ...ing,
              [name]: updatedValue,
              // alpha_composition: ing.alpha_composition || "",
              updated_at: new Date().toISOString(),
            }
          : ing
      )
    );
  };

  const handleEditMode = () => {
    if (!editMode) {
      setOriginalOrder([...ingredients]); // Store order when entering edit mode
    }
    setOriginalProduct(product);
    setEditMode(!editMode);
  };

  const handleCancelEdit = () => {
    setProduct(originalProduct); // Reset to original product
    setIngredients([...originalOrder]);
    setIdentifiedUses(product.identified_uses);
    setCustomIdentifiedUses("");
    setShowIdentifiedInput(false);

    setAppearance(product.appearance);
    setCustomAppearance("");
    setShowAppearanceInput(false);

    setMixtures(product.mixtures);
    setCustomMixtures("");
    setShowMixturesInput(false);

    setColor(product.color);
    setCustomColor("");
    setShowColorInput(false);
    setEditMode(false);
    setProductName(product.product_name);
  };

  const handleAddIngredients = () => {
    const newIngredient = {
      product_name: ingredients[0].product_name,
      identified_uses: ingredients[0].identified_uses,
      mixtures: ingredients[0].mixtures,
      appearance: ingredients[0].appearance,
      color: ingredients[0].color,
      symbol_id: ingredients[0].symbol_id,
      product_id: ingredients[0].symbol_id,
      ing_item_code: "",
      seq_no: ingredients.length + 1,
      ing_name: "",
      other_ing: 0,
      per_composition: 0,
      alpha_composition: "",
      created_at: null,
    };
    setIngredients([...ingredients, newIngredient]);
  };

  const handleRemoveIngredient = (index) => {
    // const values = [...ingredients];
    // values.splice(index, 1);
    // setIngredients(values);
    setIngredients(
      (prev) =>
        prev
          .filter((ing) => ing.seq_no !== index)
          .map((ing, newIndex) => ({ ...ing, seq_no: newIndex + 1 })) // Reset seq_no
    );
  };

  if (error) {
    return <p className="text-red-500 text-center">{error}</p>;
  }

  if (!product) {
    return <Loader />;
  }

  const handleProductDetailChange = (e, symbolData) => {
    const { name, value } = e.target;

    setProduct((prev) => ({
      ...prev,
      [name]: name === "symbol_id" ? Number(value) : value,
    }));
  };

  // Create a dynamic regex based on the symbols in symbolData
  const createSymbolRegex = (symbols) => {
    const escapedSymbols = symbols
      .filter((s) => s.symbol.length > 1 || /[^\d]/.test(s.symbol)) // Exclude pure digits like "0"
      .map((s) => s.symbol.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    // .map((s) =>
    //   s.symbol.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    // );
    return new RegExp(escapedSymbols.join("|"), "g"); //TM|C|0
  };

  const sanitizeHtmlContent = (html, symbolRegex) => {
    return html
      .replace(/<span\s+class=["']ql_cursor["']\s*><\/span>/g, "") // Remove cursor spans
      .replace(/[\u00A0\u200B\uFEFF]/g, "") // Remove invisible spaces
      .replace(new RegExp(`\\s+(?=${symbolRegex.source})`, "g"), ""); // Remove space before symbol
  };


  const placeholderSymbol = { symbol: "0", symbol_code: "0000", symbol_id: 0 };

  const handleSymbolChange = (symbolObj, index) => {
    // setSelectedSymbols((prevSelected) => {
    //   const quillInstance = quillRefs.current[index]?.getEditor();
    //   if (!quillInstance) return prevSelected;

    //   // Check if the symbol is already selected
    //   const isAlreadySelected = prevSelected.some(
    //     (s) => s.symbol_id === symbolObj.symbol_id
    //   );

   

    //   const symbolRegex = createSymbolRegex(symbolData);
    //   const plainText = quillInstance.getText().trim();
    //   // Remove trailing newline for accurate cursor positioning
    //   if (plainText.endsWith("\n")) {
    //     plainText = plainText.slice(0, -1);
    //   }
    //   let cursorIndex =
    //     quillInstance.getSelection()?.index ?? quillInstance.getLength();
    //   // Ensure cursorIndex is not beyond the trimmed text length
    //   cursorIndex = Math.min(cursorIndex, plainText.length);

    //   // Remove any existing symbols (enforce single symbol)
    //   const match = plainText.match(symbolRegex);
    //   if (match) {
    //     const matchIndex = plainText.indexOf(match[0]);
    //     quillInstance.deleteText(matchIndex, match[0].length);
    //     // Adjust cursor index if it was after the deleted symbol
    //     if (cursorIndex > matchIndex) {
    //       cursorIndex = Math.max(cursorIndex - match[0].length, matchIndex);
    //     }
    //   }

    //   // Update selected symbol(s)
    //   let updatedSymbols;
    //   if (isAlreadySelected) {
    //     // Deselect the symbol (no symbol inserted)
    //     updatedSymbols = [{ symbol: "0", symbol_code: "0000", symbol_id: 0 }];
    //   } else {
    //     // Otherwise, add the new symbol and remove any old ones
    //     setIsInsertingSymbol(true); // Start inserting
    //     updatedSymbols = [symbolObj];

    //     const rawCode = symbolObj?.symbol_code;
    //     const codePoint = parseInt(rawCode, 16); // <-- base 16 (hex)

    //     if (Number.isInteger(codePoint) && codePoint > 0) {
    //       const char = String.fromCodePoint(codePoint);
    //       quillInstance.insertText(cursorIndex, char, "user");
    //       quillInstance.setSelection(cursorIndex + char.length, 0); // Move cursor after symbol
    //     } else {
    //       console.warn("⚠️ Invalid hex symbol_code:", rawCode);
    //       setIsInsertingSymbol(false);
    //       return prevSelected;
    //     }
    //     setIsInsertingSymbol(false);
    //   }

    //   // Clean and update the product name, ensuring no multiple symbols
    //   const rawHTML = quillInstance.root.innerHTML;
    //   const cleanHTML = sanitizeHtmlContent(rawHTML, symbolRegex);
    //   setProductName(cleanHTML);

    //   return updatedSymbols;
    // });

 

    const quillInstance = quillRefs.current[index]?.getEditor();
  if (!quillInstance) return;

  quillInstance.focus();
  const selection = quillInstance.getSelection();
  const cursorIndex = selection?.index ?? quillInstance.getLength();

  const isSelected = selectedSymbols.some(
    (s) => s.symbol_id === symbolObj.symbol_id
  );

  setIsInsertingSymbol(true);

  if (!isSelected) {
    // Select new symbol
    setSelectedSymbols([symbolObj]);

    // Remove previous symbols and trailing newline
    let fullText = quillInstance.getText();
    if (fullText.endsWith("\n")) fullText = fullText.slice(0, -1);
    const textWithoutSymbols = fullText
      .replace(createSymbolRegex(symbolData), "")
      .trim();

    quillInstance.setText(textWithoutSymbols);

    // Insert new symbol at cursor
    const rawCode = symbolObj?.symbol_code;
    const codePoint = parseInt(rawCode, 16);
    if (!Number.isInteger(codePoint) || codePoint <= 0) {
      console.warn("Invalid hex symbol_code:", rawCode);
      setIsInsertingSymbol(false);
      return;
    }
    const char = String.fromCodePoint(codePoint);
    const insertIndex = Math.min(cursorIndex, textWithoutSymbols.length);
    quillInstance.insertText(insertIndex, char, "user");
    quillInstance.setSelection(insertIndex + char.length, 0); // Cursor after symbol

    // Update product name
    const rawHTML = quillInstance.root.innerHTML;
    const cleanHTML = sanitizeHtmlContent(rawHTML, createSymbolRegex(symbolData));
    setProductName(cleanHTML);
  } else {
    // Deselect symbol
    setSelectedSymbols([placeholderSymbol]);

    // Remove existing symbol
    let fullText = quillInstance.getText();
    if (fullText.endsWith("\n")) fullText = fullText.slice(0, -1);
    const textWithoutSymbols = fullText
      .replace(createSymbolRegex(symbolData), "")
      .trim();

    quillInstance.setText(textWithoutSymbols);

    // Update product name
    const rawHTML = quillInstance.root.innerHTML;
    const cleanHTML = sanitizeHtmlContent(rawHTML, createSymbolRegex(symbolData));
    setProductName(cleanHTML);
  }

  setIsInsertingSymbol(false);
  };


  const getPlainTextFromQuill = (htmlContent) => {
    const tempElement = document.createElement("div");
    tempElement.innerHTML = htmlContent;
    return tempElement.textContent || tempElement.innerText || "";
  };

  const handleProductNameChange = (htmlContent) => {
    // const plainText = getPlainTextFromQuill(htmlContent).trim();

    // const symbolMatch = plainText.match(createSymbolRegex(symbolData));

    // if (selectedSymbols) {
    //   const symbolIndex = selectedSymbols.some((symbolObj) =>
    //     plainText.includes(symbolObj.symbol)
    //   );

    //   // If symbol is manually removed
    //   if (symbolIndex === -1) {
    //     setSelectedSymbols([
    //       { symbol: "0", symbol_code: "0000", symbol_id: 0 },
    //     ]);
    //   }
    // }

    // if (selectedSymbols && symbolMatch) {
    //   const symbolStartIndex = plainText.indexOf(symbolMatch[0]);

    //   // If user is typing after the symbol
    //   if (
    //     symbolStartIndex !== -1 &&
    //     symbolStartIndex + symbolMatch[0].length < plainText.length
    //   ) {
    //     const cleanInput = plainText
    //       .replace(createSymbolRegex(symbolData), "")
    //       .trim();
    //     setSelectedSymbols([
    //       { symbol: "0", symbol_code: "0000", symbol_id: 0 },
    //     ]);
    //     setProductName(htmlContent); // This loses the new character

    //     // // FIX: Keep what user just typed
    //     // const userTypedChar = e.nativeEvent.data;
    //     // if (userTypedChar) {
    //     //   setProductName(cleanInput + userTypedChar);
    //     // } else {
    //     //   setProductName(cleanInput);
    //     // }
    //   } else {
    //     setProductName(htmlContent);
    //   }
    // } else {
    //   setProductName(htmlContent);
    // }

    if (isInsertingSymbol) {
      console.log("Currently inserting symbol, skipping...");
      return;
    }

   

    // Step 1: Remove invisible characters and ql_cursor spans
    let cleanedHtml = htmlContent
      .replace(/<span\s+class=["']ql_cursor["']\s*><\/span>/g, "")
      .replace(/[\u00A0\u200B\uFEFF]/g, "")
      .replace(/\s+(?=(${createSymbolRegex(symbolData).source}))/g, ""); // Remove space before symbols

    // Step 2: Get plain text for validation
    const plainText = getPlainTextFromQuill(cleanedHtml).trim();
    const symbolMatches = plainText.match(createSymbolRegex(symbolData));

    if (symbolMatches && symbolMatches.length > 1) {
      const firstSymbol = symbolMatches[0];
      const firstSymbolIndex = plainText.indexOf(firstSymbol);
      let updatedText = plainText.substring(
        0,
        firstSymbolIndex + firstSymbol.length
      );
      for (let i = 1; i < symbolMatches.length; i++) {
        updatedText = updatedText.replace(symbolMatches[i], "");
      }
      const quillInstance = quillRefs.current[0]?.getEditor();
      if (quillInstance) {
        quillInstance.setText(updatedText);
        cleanedHtml = quillInstance.root.innerHTML;
      }
    }
  
    // Update selectedSymbols based on text content
    const symbolMatch = symbolMatches ? symbolMatches[0] : null;
    if (symbolMatch) {
      const foundSymbol = symbolData.find(
        (symbolObj) => symbolMatch === symbolObj.symbol
      );
      if (
        foundSymbol &&
        foundSymbol.symbol_id !== selectedSymbols[0]?.symbol_id
      ) {
        setSelectedSymbols([foundSymbol]);
      }
    } else if (selectedSymbols[0]?.symbol_id !== placeholderSymbol.symbol_id) {
      setSelectedSymbols([placeholderSymbol]);
    }
  
    // Update product name
    setProductName(cleanedHtml);

    // // Step 3: Ensure only one symbol is present
    // let updatedText = plainText;
    // if (symbolMatches && symbolMatches.length > 1) {
    //   // Keep only the first symbol
    //   const firstSymbol = symbolMatches[0];
    //   const firstSymbolIndex = plainText.indexOf(firstSymbol);
    //   updatedText = plainText.substring(
    //     0,
    //     firstSymbolIndex + firstSymbol.length
    //   );
    //   for (let i = 1; i < symbolMatches.length; i++) {
    //     updatedText = updatedText.replace(symbolMatches[i], "");
    //   }
    //   const quillInstance = quillRefs.current[0]?.getEditor();
    //   if (quillInstance) {
    //     quillInstance.setText(updatedText);
    //     cleanedHtml = quillInstance.root.innerHTML;
    //   }
    // }

    // // Step 4: Update selectedSymbols based on current content
    // const symbolFound = symbolMatches ? symbolMatches[0] : null;
    // if (symbolFound) {
    //   const symbolObj = symbolData.find((item) => item.symbol === symbolFound);
    //   if (symbolObj) {
    //     setSelectedSymbols([symbolObj]);
    //   } else {
    //     setSelectedSymbols([
    //       { symbol: "0", symbol_code: "0000", symbol_id: 0 },
    //     ]);
    //   }
    // } else {
    //   setSelectedSymbols([{ symbol: "0", symbol_code: "0000", symbol_id: 0 }]);
    // }

    // // Step 5: Update product name
    // setProductName(cleanedHtml);

    // // Step 3: Handle symbol deletion by user
    // if (selectedSymbols) {
    //   const symbolIndex = selectedSymbols.some((symbolObj) =>
    //     plainText.includes(symbolObj.symbol)
    //   );

    //   if (symbolIndex === -1) {
    //     setSelectedSymbols([
    //       { symbol: "0", symbol_code: "0000", symbol_id: 0 },
    //     ]);
    //   }
    // }

    // // Step 4: Detect typing after symbol
    // if (selectedSymbols && symbolMatch) {
    //   const symbolStartIndex = plainText.indexOf(symbolMatch[0]);
    //   if (
    //     symbolStartIndex !== -1 &&
    //     symbolStartIndex + symbolMatch[0].length < plainText.length
    //   ) {
    //     const cleanInput = plainText
    //       .replace(createSymbolRegex(symbolData), "")
    //       .trim();
    //     setSelectedSymbols([
    //       { symbol: "0", symbol_code: "0000", symbol_id: 0 },
    //     ]);
    //     setProductName(cleanInput);
    //   } else {
    //     setProductName(cleanedHtml);
    //   }
    // } else {
    //   setProductName(cleanedHtml);
    // }
  };

  return (
    <div className="min-h-screen  bg-slate-700 flex flex-col items-center">
      <div className="my-10">
        <button
          onClick={() => navigate("/database")}
          className="absolute top-4 left-4 bg-gray-800 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
        >
          Back
        </button>
      </div>

      <div className="flex justify-center items-center p-4 my-auto overflow-auto w-full">
        <div className="max-w-4xl w-full bg-slate-800 rounded-lg text-white shadow-lg p-6">
          <div className="grid grid-cols-[1fr_2fr] max-sm:grid-cols-1 gap-4 items-center mt-3">
            {role === "admin" && editMode ? (
              <>
                <div className="font-semibold">Product Name : </div>
                <span className="flex flex-col mt-4">
                  <ReactQuill
                    ref={(el) => (quillRefs.current[0] = el)}
                    name="ing_name"
                    value={productName || ""}
                    onChange={(content) => handleProductNameChange(content)}
                    theme="snow"
                    // className="bg-gray-500 text-white  w-full "
                    className=" text-gray-200 w-full"
                    required
                  />
                  <span className="flex flex-wrap gap-4">
                    {symbolData.length > 0 ? (
                      symbolData
                        .filter((item) => item.symbol_id !== 0)
                        .map((symbolObj) => (
                          <label
                            key={symbolObj.symbol_id}
                            className="flex items-center space-x-2"
                          >
                            <input
                              type="checkbox"
                              name="symbol_id"
                              checked={selectedSymbols.some(
                                (selected) =>
                                  selected.symbol_id === symbolObj.symbol_id
                              )}
                              onChange={(e) => handleSymbolChange(symbolObj, 0)}
                            />
                            <span className="text-xl">{symbolObj.symbol}</span>
                          </label>
                        ))
                    ) : (
                      <p className="text-gray-400">No symbols available</p>
                    )}
                  </span>
                </span>
              </>
            ) : (
              <h1
                className="text-2xl font-bold mb-4 whitespace-nowrap"
                dangerouslySetInnerHTML={{ __html: product.product_name }}
              ></h1>
            )}
          </div>
          <div className="grid grid-cols-[1fr_2fr] max-sm:grid-cols-1 gap-4 items-center mt-3">
            <div className="font-semibold">Product ID : </div>

            {role === "admin" && editMode ? (
              <span>
                <input
                  name="product_id"
                  type="text"
                  value={product.product_id}
                  onChange={(e) => handleProductDetailChange(e)}
                  maxLength={15}
                  className="border p-2 rounded bg-gray-700 w-full"
                  required
                />
              </span>
            ) : (
              <span className="text-white">{product.product_id}</span>
            )}

            <div>
              {" "}
              <label className="font-semibold">Identified Uses : </label>
            </div>

            {(role === "admin" || role === "user") && editMode ? (
              <span>
                <select
                  className="border p-2 rounded bg-gray-700 w-full"
                  value={showIdentifiedInput ? "Other" : identifiedUses}
                  onChange={(e) =>
                    handleDropDown(
                      e.target.value,
                      setShowIdentifiedInput,
                      setIdentifiedUses,
                      setCustomIdentifiedUses
                    )
                  }
                >
                  {customIdentifiedUses ? (
                    <option value="">Select</option>
                  ) : (
                    <option value="" disabled>
                      Select
                    </option>
                  )}
                  {identifiedUsesData.map((currElem, idx) => {
                    return (
                      <option value={currElem} key={idx}>
                        {currElem}
                      </option>
                    );
                  })}
                  <option value="Other">Other</option>
                </select>

                {showIdentifiedInput && (
                  <input
                    type="text"
                    value={customIdentifiedUses}
                    onChange={(e) => setCustomIdentifiedUses(e.target.value)}
                    onKeyDown={(e) =>
                      handleInputEvent(
                        e,
                        customIdentifiedUses,
                        setIdentifiedUses,
                        setShowIdentifiedInput
                      )
                    }
                    className="border p-2 rounded bg-gray-700 mt-2 w-full"
                    placeholder="Enter custom value"
                    autoFocus
                  />
                )}
                {identifiedUses &&
                  !identifiedUsesData.includes(identifiedUses) &&
                  !showIdentifiedInput && (
                    <span className="mt-2 text-gray-300 ">
                      Selected:{" "}
                      <span className="text-xl font-bold">
                        {identifiedUses}
                      </span>{" "}
                    </span>
                  )}
              </span>
            ) : (
              <span className=" text-white">{product.identified_uses}</span>
            )}

            {/* <span className="font-semibold">
              {product.identified_uses}
            </span> */}

            {/* 
            {
              role === 'admin' ?
                (
                  <span className="font-semibold">
                    Mixtures : <span>
                      <input
                        type="text"
                        name="mixtures"
                        value={product.mixtures}
                        className="p-2 bg-gray-600 rounded border border-gray-400 font-light"
                      />
                    </span>
                  </span>
                ) : ( */}

            <div>
              <label className="font-semibold">Mixtures: Synonyms : </label>
            </div>

            {(role === "admin" || role === "user") && editMode ? (
              <span>
                <select
                  className="border p-2 rounded bg-gray-700 w-full"
                  value={showMixturesInput ? "Other" : mixtures}
                  onChange={(e) =>
                    handleDropDown(
                      e.target.value,
                      setShowMixturesInput,
                      setMixtures,
                      setCustomMixtures
                    )
                  }
                >
                  {customMixtures ? (
                    <option value="">Select</option>
                  ) : (
                    <option value="" disabled>
                      Select
                    </option>
                  )}
                  {mixturesData.map((currElem, index) => {
                    return (
                      <option value={currElem} key={index}>
                        {currElem}
                      </option>
                    );
                  })}
                  <option value="Other">Other (please specify)</option>
                </select>

                {showMixturesInput && (
                  <input
                    type="text"
                    value={customMixtures}
                    onChange={(e) => setCustomMixtures(e.target.value)}
                    onKeyDown={(e) =>
                      handleInputEvent(
                        e,
                        customMixtures,
                        setMixtures,
                        setShowMixturesInput
                      )
                    }
                    className="border p-2 rounded bg-gray-700 mt-2 w-full"
                    placeholder="Enter custom value"
                    autoFocus
                  />
                )}

                {mixtures &&
                  !mixturesData.includes(mixtures) &&
                  !showMixturesInput && (
                    <span className="mt-2 text-gray-300 ">
                      Selected:{" "}
                      <span className="text-xl font-bold">{mixtures}</span>{" "}
                    </span>
                  )}
              </span>
            ) : (
              <span className="text-white">{product.mixtures}</span>
            )}
            {/* <span className="font-semibold">
              {product.mixtures}
            </span> */}

            {/*                 )
            } */}

            <div>
              {" "}
              <label className="font-semibold">Appearance : </label>
            </div>

            {(role === "admin" || role === "user") && editMode ? (
              <span>
                <select
                  className="border p-2 rounded bg-gray-700 w-full"
                  value={showAppearanceInput ? "Other" : appearance}
                  onChange={(e) =>
                    handleDropDown(
                      e.target.value,
                      setShowAppearanceInput,
                      setAppearance,
                      setCustomAppearance
                    )
                  }
                >
                  {customAppearance ? (
                    <option value="">Select</option>
                  ) : (
                    <option value="" disabled>
                      Select
                    </option>
                  )}

                  {appearanceData.map((currElem, index) => {
                    return (
                      <option value={currElem} key={index}>
                        {currElem}
                      </option>
                    );
                  })}
                  <option value="Other">Other (please specify)</option>
                </select>

                {showAppearanceInput && (
                  <input
                    type="text"
                    value={customAppearance}
                    onChange={(e) => setCustomAppearance(e.target.value)}
                    onKeyDown={(e) =>
                      handleInputEvent(
                        e,
                        customAppearance,
                        setAppearance,
                        setShowAppearanceInput
                      )
                    }
                    className="border p-2 rounded bg-gray-700 mt-2 w-full"
                    placeholder="Enter custom value"
                    autoFocus
                  />
                )}

                {appearance &&
                  !appearanceData.includes(appearance) &&
                  !showAppearanceInput && (
                    <span className="mt-2 text-gray-300 ">
                      Selected:{" "}
                      <span className="text-xl font-bold">{appearance}</span>{" "}
                    </span>
                  )}
              </span>
            ) : (
              <span className="text-white">{product.appearance}</span>
            )}

            <div>
              <label className="font-semibold">Colour : </label>
            </div>

            {(role === "admin" || role === "user") && editMode ? (
              <span>
                <select
                  className="border p-2 rounded bg-gray-700 w-full"
                  value={showColorInput ? "Other" : color}
                  onChange={(e) =>
                    handleDropDown(
                      e.target.value,
                      setShowColorInput,
                      setColor,
                      setCustomColor
                    )
                  }
                >
                  {customColor ? (
                    <option value="">Select</option>
                  ) : (
                    <option value="" disabled>
                      Select
                    </option>
                  )}

                  {colorData.map((currElem, index) => {
                    return (
                      <option value={currElem} key={index}>
                        {currElem}
                      </option>
                    );
                  })}
                  <option value="Other">Other (please specify)</option>
                </select>

                {showColorInput && (
                  <input
                    type="text"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    onKeyDown={(e) =>
                      handleInputEvent(
                        e,
                        customColor,
                        setColor,
                        setShowColorInput
                      )
                    }
                    className="border p-2 rounded bg-gray-700 mt-2 w-full"
                    placeholder="Enter custom value"
                    autoFocus
                  />
                )}

                {color && !colorData.includes(color) && !showColorInput && (
                  <span className="mt-2 text-gray-300 ">
                    Selected: <span className="text-xl font-bold">{color}</span>{" "}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-white">{product.color}</span>
            )}
          </div>

          <h2 className="text-xl font-bold mt-6 mb-10">Ingredient Details</h2>

          <div className="overflow-auto" style={{ touchAction: "none" }}>
            <DndContext
              onDragEnd={handleDragEnd}
              sensors={sensors}
              collisionDetection={closestCenter}
              measuring={measuringConfig}
            >
              <SortableContext
                items={ingredients.map((ing) => ing.seq_no)}
                strategy={verticalListSortingStrategy}
              >
                <table className="w-full text-white text-center">
                  <thead>
                    <tr>
                      <th className="border-b border-slate-600 px-4 py-2">
                        Drag
                      </th>
                      {/* <th className="border-b border-slate-600 px-4 py-2">Sequence No</th> */}
                      <th className="border-b border-slate-600 px-4 py-2">
                        Item Code
                      </th>
                      <th className="border-b border-slate-600 px-4 py-2">
                        Ingredient Name
                      </th>
                      <th className="border-b border-slate-600 px-4 py-2">
                        Composition in Numerical
                      </th>
                      <th className="border-b border-slate-600 px-4 py-2">
                        Percentage Composition
                      </th>
                      <th className="border-b border-slate-600 px-4 py-2">
                        Other Ingredient
                      </th>
                      {role === "admin" && editMode && (
                        <th className="border-b border-slate-600 px-4 py-2">
                          Delete Ingredients
                        </th>
                      )}

                      {/* <th className="border-b border-slate-600 px-4 py-2">Actions</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {ingredients.map((ingredient) => (
                      <SortableItem
                        key={ingredient.seq_no}
                        ingredient={ingredient}
                        onEditChange={handleEditChange}
                        editMode={editMode}
                        setEditMode={setEditMode}
                        setIngredients={setIngredients} // Pass state setter
                        originalOrder={originalOrder} // Pass original order
                        isSaving={isSaving}
                        handleRemoveIngredient={handleRemoveIngredient}
                      />
                    ))}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>

          {(role === "admin" || role === "user") && !editMode ? (
            <div className="mt-6 flex justify-between gap-4">
              <button
                onClick={handleEditMode}
                className={`${
                  editMode ? "hidden" : "block"
                } bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-6 rounded `}
              >
                Edit
              </button>
            </div>
          ) : (
            role === "admin" &&
            editMode && (
              <>
                <div className="flex justify-center sm:justify-start mt-6">
                  <button
                    onClick={handleAddIngredients}
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded w-full sm:w-auto"
                  >
                    Add Ingredients
                  </button>
                </div>
              </>
            )
          )}
          {(role === "admin" || role === "user") && editMode && (
            <>
              <div className="flex flex-col sm:flex-row justify-between my-8 space-y-2 sm:space-y-0">
                {/* Left Buttons */}
                <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0 w-full sm:w-auto">
                  <button
                    onClick={handleCancelEdit}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded w-full sm:w-auto"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handlePatch}
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded w-full sm:w-auto"
                  >
                    Update
                  </button>
                </div>
                {role === "admin" && (
                  <div className="flex justify-center sm:justify-end w-full sm:w-auto">
                    <button
                      onClick={handleDelete}
                      className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-4 rounded w-full sm:w-auto flex justify-center items-center"
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Productitem;
